// src/components/Finanzas/MiBolsa.jsx
// Pantalla principal del responsable.
// Refactor 2: estructura inspirada en MiRelacionViaticos
//   1. Cabecera del bolsillo con saldo grande
//   2. 4 cards de resumen (Recibido / Gastado / Saldo / % Gastado + ProgressBar)
//   3. Alertas (sobregiro, > 80%, CON_DEUDA)
//   4. Banner de pendientes (entregas + traslados a confirmar)
//   5. Acciones principales
//   6. Card dedicado: Traslados de Dinero (tabla con recibidos + enviados)
//   7. Card dedicado: Mis Gastos Registrados (Accordion por categoría)
//   8. (Plegable al fondo) Actividad reciente del ledger por mes

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Badge, Alert,
  ProgressBar, Accordion
} from 'react-bootstrap';
import {
  FaMoneyBillWave, FaReceipt, FaChartLine, FaFileInvoiceDollar,
  FaExchangeAlt, FaCheck, FaBan, FaPlus, FaClipboardCheck, FaWallet,
  FaArrowDown, FaArrowUp, FaHistory, FaEdit, FaTrash,
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaEye
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { Alertassw } from '../Alertas/Alertassw';
import { manejarConfigFaltanteSi422 } from '../Alertas/swalConfigContableFaltante';
import {
  getBolsilloMio,
  getBolsilloActuales,
  confirmarEntrega,
  rechazarEntrega,
  confirmarTraslado,
  rechazarTraslado,
  anularGasto,
  anularTraslado,
  iniciarRelacion,
  obtenerEntrega,
  obtenerTrasladoOps
} from '../../api/finanzas';
import { useAuth } from '../../hooks/useAuth';
import { useNotificacionesFinanzas } from '../../hooks/useNotificacionesFinanzas';
import { extractError } from './utils/extractError';
import ModalRegistrarMovimiento from './ModalRegistrarMovimiento';
import ModalConfirmacionMovimiento from './ModalConfirmacionMovimiento';
import ModalObservacionesMovimiento from './ModalObservacionesMovimiento';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const MiBolsa = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();

  const [bolsilloData, setBolsilloData] = useState(null);  // del endpoint /Bolsillo/mio
  const [gastos, setGastos] = useState([]);                 // gastos del periodo actual
  const [traslados, setTraslados] = useState([]);           // traslados del periodo actual
  const [entregas, setEntregas] = useState([]);             // entregas del periodo actual
  const [enBorrador, setEnBorrador] = useState({ gastos: [], traslados: [], entregas: [] });
  const [saldoInicialPeriodo, setSaldoInicialPeriodo] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showRegistrar, setShowRegistrar] = useState(false);
  const [gastoEditar, setGastoEditar] = useState(null);     // null = modo crear, objeto = modo editar
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  const [pendienteEnConfirmacion, setPendienteEnConfirmacion] = useState(null);
  const [tipoEnConfirmacion, setTipoEnConfirmacion] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [refresh, setRefresh] = useState(0);

  // Modal de observaciones (FaEye en tabla Movimientos + ledger auditoría).
  const [observacionesMov, setObservacionesMov] = useState({
    show: false,
    movimiento: null,
    tipo: null,
    loading: false
  });

  // Tiempo real: el Provider global escucha los eventos Socket.io y expone
  // `bolsilloUpdate` cada vez que algo relevante cambia (entregas/traslados
  // confirmados o rechazados, BOLSILLO_ACTUALIZADO/BOLSILLO_CON_DEUDA).
  const { bolsilloUpdate } = useNotificacionesFinanzas();

  useEffect(() => {
    cargar();
  }, [refresh]);

  useEffect(() => {
    if (bolsilloUpdate) {
      setRefresh((r) => r + 1);
    }
  }, [bolsilloUpdate]);

  const cargar = async () => {
    setLoading(true);
    try {
      // Header del bolsillo + pendientes + ledger reciente (sigue trayendo todo
      // porque el ledger es auditoria) y a la par los items "actuales" del
      // periodo abierto (gastos / traslados / entregas que no estan en una
      // relacion APROBADA+). Asi /MiBolsa refleja el periodo en curso, y los
      // históricos viven en /MiBolsa/historial.
      const [respMio, respActuales] = await Promise.all([
        getBolsilloMio(),
        getBolsilloActuales()
      ]);

      const data = respMio?.data?.data || null;
      setBolsilloData(data);

      const actuales = respActuales?.data?.data;
      if (actuales?.configurado) {
        setGastos(actuales.gastos || []);
        setTraslados(actuales.traslados || []);
        setEntregas(actuales.entregas || []);
        setEnBorrador(actuales.enBorrador || { gastos: [], traslados: [], entregas: [] });
        setSaldoInicialPeriodo(actuales.saldoInicialPeriodo || 0);
      } else {
        setGastos([]);
        setTraslados([]);
        setEntregas([]);
        setEnBorrador({ gastos: [], traslados: [], entregas: [] });
        setSaldoInicialPeriodo(0);
      }
    } catch (err) {
      console.error('Error cargar bolsillo:', err);
      Alertassw('error', 'Error', extractError(err, 'No se pudo cargar el bolsillo'));
    } finally {
      setLoading(false);
    }
  };

  // Helpers visuales: marcar items que ya quedaron capturados en el borrador
  // de cierre. Se siguen mostrando aqui (UX "el cierre todavia se puede
  // editar"), pero el badge avisa que estan apartados para el borrador.
  const estaEnBorrador = (id, tipo) => {
    const lista = enBorrador?.[tipo];
    if (!lista || !lista.length) return false;
    return lista.includes(String(id));
  };

  // ============================================
  // Modal de observaciones (FaEye)
  // ============================================
  const tieneObservacionesTraslado = (t) =>
    !!(t?.confirmacion?.observaciones || t?.rechazo?.motivoRechazo);
  const tieneObservacionesEntrega = (e) =>
    !!(e?.confirmacion?.observacionesReceptor || e?.rechazo?.motivoRechazo);

  // Para la tabla "Movimientos de Dinero": data ya hidratada, no hay fetch.
  const abrirObservacionesDirecto = (movimiento, tipo) => {
    setObservacionesMov({ show: true, movimiento, tipo, loading: false });
  };

  // Para el ledger "Ver ledger completo": fetch lazy del traslado/entrega
  // original a partir de m.origen.refId. El backend ya excluye la firma
  // digital y valida ownership (ver mitigación S-CRIT-01 / S-CRIT-02).
  const abrirObservacionesDesdeLedger = async (ledgerItem) => {
    const refTipo = ledgerItem?.origen?.tipo;
    const refId   = ledgerItem?.origen?.refId;
    if (!refTipo || !refId) return; // Tipos sin referencia (GastoContable, AjusteManual) no exponen modal.

    const esTraslado = refTipo === 'TrasladoDinero';
    const esEntrega  = refTipo === 'EntregaDinero';
    if (!esTraslado && !esEntrega) return;

    setObservacionesMov({ show: true, movimiento: null, tipo: esTraslado ? 'traslado' : 'entrega', loading: true });
    try {
      const resp = esTraslado ? await obtenerTrasladoOps(refId) : await obtenerEntrega(refId);
      const doc = resp?.data?.data || null;
      setObservacionesMov({
        show: true,
        movimiento: doc,
        tipo: esTraslado ? 'traslado' : 'entrega',
        loading: false
      });
    } catch (err) {
      console.error('Error cargando movimiento del ledger:', err);
      Alertassw('error', 'Error', extractError(err, 'No se pudieron cargar las observaciones'));
      setObservacionesMov({ show: false, movimiento: null, tipo: null, loading: false });
    }
  };

  const cerrarObservaciones = () =>
    setObservacionesMov({ show: false, movimiento: null, tipo: null, loading: false });

  // ============================================
  // Handlers pendientes
  // ============================================
  const abrirConfirmacion = (pendiente, tipo) => {

    setPendienteEnConfirmacion(pendiente);
    setTipoEnConfirmacion(tipo);
    setShowConfirmacion(true);
  };

  const handleConfirmar = async (datosFirma) => {
    if (!pendienteEnConfirmacion) return;
    setProcessingAction(true);
    try {
      const fn = tipoEnConfirmacion === 'entrega' ? confirmarEntrega : confirmarTraslado;
      const resp = await fn(pendienteEnConfirmacion._id, datosFirma);
      if (resp?.status === 200) {
        Alertassw('success', 'Confirmado', `${tipoEnConfirmacion === 'entrega' ? 'Entrega' : 'Traslado'} confirmado correctamente.`);
        setShowConfirmacion(false);
        setRefresh((r) => r + 1);
      }
    } catch (err) {
      console.error(err);
      // Si el backend devuelve 422 CONFIG_CONTABLE_INCOMPLETA, mostrar
      // SwalAlert con los campos faltantes y ofrecer ir a /ConfiguracionContable.
      // En ese caso NO mostramos el mensaje generico.
      if (await manejarConfigFaltanteSi422(err, navigate)) {
        setShowConfirmacion(false);
      } else {
        Alertassw('error', 'Error al confirmar', extractError(err));
      }
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRechazar = async (pendiente, tipo) => {
    const { value: motivo } = await Swal.fire({
      title: '¿Rechazar?',
      input: 'textarea',
      inputLabel: 'Motivo del rechazo',
      inputPlaceholder: 'Escribe el motivo...',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar'
    });
    if (!motivo) return;
    setProcessingAction(true);
    try {
      const fn = tipo === 'entrega' ? rechazarEntrega : rechazarTraslado;
      await fn(pendiente._id, motivo);
      Alertassw('success', 'Rechazado', 'La transacción fue rechazada.');
      setRefresh((r) => r + 1);
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setProcessingAction(false);
    }
  };

  // ============================================
  // Handler anular traslado pendiente (sender cancela su propio envio)
  // ============================================
  const handleAnularTraslado = async (traslado) => {
    const result = await Swal.fire({
      title: '¿Anular este traslado?',
      html: `
        <div style="text-align: left;">
          <p><strong>Monto:</strong> ${formatoCOP(traslado.monto)}</p>
          <p><strong>Destinatario:</strong> ${traslado.cuenta_destino?.nombre || '—'}</p>
        </div>
        <p class="text-muted small mt-2">El traslado se cancela antes de que el destinatario lo confirme.</p>
      `,
      input: 'textarea',
      inputLabel: 'Justificación',
      inputPlaceholder: 'Motivo de la anulación...',
      inputValidator: (v) => !v && 'Debes indicar el motivo',
      showCancelButton: true,
      confirmButtonText: 'Anular',
      confirmButtonColor: '#dc3545',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed || !result.value) return;
    setProcessingAction(true);
    try {
      await anularTraslado(traslado._id, result.value);
      Alertassw('success', 'Traslado anulado', 'El traslado fue cancelado correctamente.');
      setRefresh((r) => r + 1);
    } catch (err) {
      Alertassw('error', 'Error al anular', extractError(err));
    } finally {
      setProcessingAction(false);
    }
  };

  // ============================================
  // Iniciar cierre de relacion de gastos
  // ============================================
  const handleCerrarGastos = async () => {
    if (!bolsilloData?.bolsillo?._id) return;

    // Si ya hay un borrador, navegar directo a revisar el existente
    if (bolsilloData.relacionBorradorActiva) {
      navigate(`/RevisionCierre/${bolsilloData.relacionBorradorActiva}`);
      return;
    }

    const result = await Swal.fire({
      title: '¿Cerrar gastos del periodo?',
      html: `
        <p>Se creara una <strong>relacion de gastos en BORRADOR</strong> con todos los
        gastos que tengas REGISTRADOS y los movimientos sin relacionar.</p>
        <p class="text-muted small">Podras revisar y editar antes de aprobar.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Iniciar cierre',
      confirmButtonColor: '#198754',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;

    setProcessingAction(true);
    try {
      const resp = await iniciarRelacion(bolsilloData.bolsillo._id);
      if (resp?.status === 200 || resp?.status === 201) {
        const relacion = resp.data?.data;
        if (relacion?._id) {
          Alertassw('success', 'Relacion creada', `Consecutivo ${relacion.consecutivo || ''}. Revisa antes de aprobar.`);
          navigate(`/RevisionCierre/${relacion._id}`);
          return;
        }
      }
      Alertassw('error', 'Error', 'No se pudo iniciar la relacion');
    } catch (err) {
      Alertassw('error', 'Error al iniciar cierre', extractError(err));
    } finally {
      setProcessingAction(false);
    }
  };

  // ============================================
  // Handlers de gasto (editar / anular)
  // ============================================
  const handleEditarGasto = (gasto) => {
    setGastoEditar(gasto);
    setShowRegistrar(true);
  };

  const handleAnularGasto = async (gasto) => {
    const result = await Swal.fire({
      title: '¿Anular este gasto?',
      html: `
        <div style="text-align: left;">
          <p><strong>Descripción:</strong> ${gasto.descripcion}</p>
          <p><strong>Valor:</strong> ${formatoCOP(gasto.valorTotal)}</p>
        </div>
        <p class="text-muted small mt-2">Se generará un ajuste para restituir el saldo en tu bolsillo. Esta acción no se puede deshacer.</p>
      `,
      input: 'textarea',
      inputLabel: 'Justificación',
      inputPlaceholder: 'Motivo de la anulación...',
      inputValidator: (v) => !v && 'Debes indicar el motivo',
      showCancelButton: true,
      confirmButtonText: 'Anular',
      confirmButtonColor: '#dc3545',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed || !result.value) return;
    setProcessingAction(true);
    try {
      await anularGasto(gasto._id, result.value);
      Alertassw('success', 'Gasto anulado', 'Tu saldo se restituyó correctamente.');
      setRefresh((r) => r + 1);
    } catch (err) {
      Alertassw('error', 'Error al anular', extractError(err));
    } finally {
      setProcessingAction(false);
    }
  };

  const cerrarModalRegistrar = () => {
    setShowRegistrar(false);
    setGastoEditar(null);
  };

  const onSuccessRegistrar = () => {
    setShowRegistrar(false);
    setGastoEditar(null);
    setRefresh((r) => r + 1);
  };

  // ============================================
  // Métricas calculadas
  // ============================================
  const totalRecibidoEntregas = entregas.reduce((s, e) => s + e.monto, 0);

  const trasladosRecibidos = traslados.filter(
    (t) => t.estado === 'CONFIRMADO' && t.cuenta_destino && (t.cuenta_destino._id || t.cuenta_destino) === bolsilloData?.bolsillo?._id
  );
  const trasladosEnviados = traslados.filter(
    (t) => t.estado === 'CONFIRMADO' && t.cuenta_origen && (t.cuenta_origen._id || t.cuenta_origen) === bolsilloData?.bolsillo?._id
  );

  const totalTrasladosRecibidos = trasladosRecibidos.reduce((s, t) => s + t.monto, 0);
  const totalTrasladosEnviados = trasladosEnviados.reduce((s, t) => s + t.monto, 0);
  const totalRecibido = totalRecibidoEntregas + totalTrasladosRecibidos;

  // Lista combinada de movimientos de dinero (entregas + traslados) para la tabla
  // Cada item tiene { _id, fecha, tipoMovimiento, contraparte, monto, signo, estado, descripcion }
  const movimientosDinero = [
    ...entregas.map((e) => ({
      _id: `e-${e._id}`,
      fecha: e.fecha,
      tipoMovimiento: 'ENTREGA',
      contraparte: e.cuentaOrigen,
      contraparteLabel: e.entregadoPor?.nombre || e.entregadoPor?.nombres,
      monto: e.monto,
      signo: '+',
      estado: e.estado,
      descripcion: e.descripcion,
      consecutivo: e.consecutivo,
      raw: e,
      soyElCreador: false,
      puedeAnular: false
    })),
    ...traslados.map((t) => {
      const esRecibido = (t.cuenta_destino?._id || t.cuenta_destino) === bolsilloData?.bolsillo?._id;
      const creadorId = t.creadoPor?._id || t.creadoPor || t.usuario?._id || t.usuario;
      const soyElCreador = userData?._id && creadorId && String(creadorId) === String(userData._id);
      return {
        _id: `t-${t._id}`,
        fecha: t.fecha,
        tipoMovimiento: esRecibido ? 'TRASLADO_RECIBIDO' : 'TRASLADO_ENVIADO',
        contraparte: esRecibido ? t.cuenta_origen : t.cuenta_destino,
        contraparteLabel: null,
        monto: t.monto,
        signo: esRecibido ? '+' : '-',
        estado: t.estado,
        descripcion: t.descripcion,
        consecutivo: t.consecutivo,
        raw: t,                  // referencia al traslado original para acciones
        soyElCreador,
        puedeAnular: !esRecibido && soyElCreador && t.estado === 'PENDIENTE_CONFIRMACION'
      };
    })
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  const totalGastado = gastos
    .filter((g) => g.estado !== 'ANULADO')
    .reduce((s, g) => s + g.valorTotal, 0);

  // Gastos REGISTRADO (no incluidos en otra relación, no anulados). Si no hay
  // ninguno y no existe borrador activo, el botón "Cerrar gastos del periodo"
  // se deshabilita — el backend respondería 400 igualmente, pero la guardia
  // ahorra el round-trip y deja claro al usuario por qué no procede.
  const gastosRegistradosCount = gastos.filter((g) => g.estado === 'REGISTRADO').length;

  const saldo = bolsilloData?.bolsillo?.saldoActual ?? 0;
  const porcentajeGastado = totalRecibido > 0 ? (totalGastado / totalRecibido) * 100 : 0;

  // Gastos agrupados por categoría
  const gastosAgrupados = gastos
    .filter((g) => g.estado !== 'ANULADO')
    .reduce((acc, g) => {
      const cat = g.categoria || 'Sin categoría';
      if (!acc[cat]) acc[cat] = { total: 0, cantidad: 0, items: [] };
      acc[cat].total += g.valorTotal;
      acc[cat].cantidad += 1;
      acc[cat].items.push(g);
      return acc;
    }, {});
  const categoriasOrdenadas = Object.entries(gastosAgrupados).sort((a, b) => b[1].total - a[1].total);

  const formatearCategoria = (cat) => cat.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  // ============================================
  // Estados condicionales
  // ============================================
  if (loading) {
    return (
      <Container fluid className="py-4 w-100">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando información de tu bolsillo...</p>
        </div>
      </Container>
    );
  }

  if (!bolsilloData || !bolsilloData.bolsillo) {
    return (
      <Container fluid className="py-4 w-100">
        <Alert variant="info" className="text-center py-5">
          <FaWallet size={50} className="mb-3 text-primary" />
          <h4>No tienes un bolsillo asignado</h4>
          <p className="mb-0">
            Pídele a un administrador que te cree una <strong>cuenta contable personal</strong> con la opción
            <em> esBolsilloPersonal </em>activada.
          </p>
        </Alert>
      </Container>
    );
  }

  const { bolsillo, pendientes, actividadReciente, relacionBorradorActiva } = bolsilloData;

  const enDeuda = bolsillo.enDeuda || saldo < 0;
  const totalPendientes = (pendientes?.entregas?.length || 0) + (pendientes?.trasladosRecibidos?.length || 0);

  return (
    <Container fluid className="py-4 w-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
        {/**Colocar un stiker de billetera despues del titulo */}
          <h2 className="text-primary fw-bold">
            <FaWallet className="ms-2" />
              Mi Bolsillo  
          </h2>
          <p className="text-muted">Gestiona tus entradas, gastos y cierres de relación</p>
        </Col>
      </Row>

      {/* Cabecera del bolsillo */}
      <Card className={`mb-4 shadow-sm border-start ${enDeuda ? 'border-danger' : 'border-primary'} border-4 w-100`}>
        <Card.Body className="w-100">
          <Row>
            <Col md={6}>
              <h5 className="fw-bold mb-3">
                {bolsillo.nombre}
                <Badge bg={enDeuda ? 'danger' : 'success'} className="ms-2">
                  {enDeuda ? 'CON DEUDA' : 'Activo'}
                </Badge>
                {bolsillo.funcion && <Badge bg="info" className="ms-2">{bolsillo.funcion}</Badge>}
              </h5>
              <p className="mb-1"><strong>Cuenta:</strong> {bolsillo.codigo}</p>
              <p className="mb-1 text-muted small">
                <FaHistory className="me-1" />
                Saldo inicial del periodo: <strong>{formatoCOP(saldoInicialPeriodo)}</strong>
                <span className="ms-1">(viene del último cierre)</span>
              </p>
              {relacionBorradorActiva && (
                <Alert variant="warning" className="py-2 mt-2 mb-0 small">
                  <FaClipboardCheck className="me-1" />
                  Tienes una <strong>relación de gastos en BORRADOR</strong>.{' '}
                  <a href={`/RevisionCierre/${relacionBorradorActiva}`} className="alert-link">Continuar cierre →</a>
                </Alert>
              )}
            </Col>
            <Col md={6}>
              <div className="text-end">
                <p className="mb-2 text-muted">Saldo disponible</p>
                <h2 className={`fw-bold mb-1 ${enDeuda ? 'text-danger' : 'text-success'}`}>
                  {formatoCOP(saldo)}
                </h2>
                {bolsillo.montoComprometido > 0 && (
                  <small className="text-warning">
                    <strong>Comprometido en traslados pendientes:</strong> {formatoCOP(bolsillo.montoComprometido)}
                  </small>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 4 Cards de Resumen Financiero */}
      <Row className="mb-4 g-2">
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <FaMoneyBillWave className="text-success mb-2" size={30} />
              <h6 className="text-muted mb-2">Total Recibido</h6>
              <h4 className="fw-bold text-success mb-0">{formatoCOP(totalRecibido)}</h4>
              <small className="text-muted">
                Entregas: {formatoCOP(totalRecibidoEntregas)}
              </small>
              {totalTrasladosRecibidos > 0 && (
                <small className="d-block text-muted">
                  Traslados: {formatoCOP(totalTrasladosRecibidos)}
                </small>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <FaReceipt className="text-primary mb-2" size={30} />
              <h6 className="text-muted mb-2">Total Gastado</h6>
              <h4 className="fw-bold text-primary mb-0">{formatoCOP(totalGastado)}</h4>
              <small className="text-muted">{gastos.filter(g => g.estado !== 'ANULADO').length} gasto(s)</small>
              {totalTrasladosEnviados > 0 && (
                <small className="d-block text-muted">
                  + {formatoCOP(totalTrasladosEnviados)} en traslados enviados
                </small>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <FaChartLine className={`mb-2 ${saldo >= 0 ? 'text-warning' : 'text-danger'}`} size={30} />
              <h6 className="text-muted mb-2">Saldo Actual</h6>
              <h4 className={`fw-bold mb-0 ${saldo >= 0 ? 'text-warning' : 'text-danger'}`}>
                {formatoCOP(Math.abs(saldo))}
              </h4>
              <small className="text-muted">
                {saldo > 0 ? 'Disponible' : saldo < 0 ? 'En deuda' : 'Cuadrado'}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <div className="mb-2">
                <Badge
                  bg={porcentajeGastado > 100 ? 'danger' : porcentajeGastado > 80 ? 'warning' : 'success'}
                  style={{ fontSize: '1.2rem' }}
                >
                  {porcentajeGastado.toFixed(1)}%
                </Badge>
              </div>
              <h6 className="text-muted mb-2">Porcentaje Gastado</h6>
              <ProgressBar
                now={porcentajeGastado > 100 ? 100 : porcentajeGastado}
                variant={porcentajeGastado > 100 ? 'danger' : porcentajeGastado > 80 ? 'warning' : 'success'}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Alertas */}
      {enDeuda && (
        <Alert variant="danger" className="d-flex align-items-center">
          <div>
            <strong>⚠️ Bolsillo en deuda:</strong> Tu saldo actual es {formatoCOP(Math.abs(saldo))} negativo.
            La próxima entrega que recibas cubrirá primero esta deuda.
          </div>
        </Alert>
      )}

      {!enDeuda && porcentajeGastado > 80 && porcentajeGastado <= 100 && (
        <Alert variant="warning">
          <strong>Atención:</strong> Has gastado más del 80% del dinero recibido. Planifica bien tus próximos gastos.
        </Alert>
      )}

      {/* Banner de pendientes */}
      {totalPendientes > 0 && (
        <Alert variant="warning" className="d-flex flex-column">
          <strong className="mb-2">
            ⚠️ Tienes {totalPendientes} {totalPendientes === 1 ? 'pendiente' : 'pendientes'} de confirmar
          </strong>
          <Table hover responsive size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Origen / Emisor</th>
                <th className="text-end">Monto</th>
                <th>Descripción</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(pendientes?.entregas || []).map((e) => (
                <tr key={e._id}>
                  <td><Badge bg="primary">Entrega</Badge></td>
                  <td>{new Date(e.fecha).toLocaleDateString('es-CO')}</td>
                  <td>{e.cuentaOrigen?.nombre || 'Cuenta maestra'}</td>
                  <td className="text-end fw-bold">{formatoCOP(e.monto)}</td>
                  <td><small>{e.descripcion || '—'}</small></td>
                  <td className="text-center">
                    <Button variant="outline-success" size="sm" className="me-1"
                      onClick={() => abrirConfirmacion(e, 'entrega')}
                      disabled={processingAction}>
                      <FaCheck />
                    </Button>
                    <Button variant="outline-danger" size="sm"
                      onClick={() => handleRechazar(e, 'entrega')}
                      disabled={processingAction}>
                      <FaBan />
                    </Button>
                  </td>
                </tr>
              ))}
              {(pendientes?.trasladosRecibidos || []).map((t) => (
                <tr key={t._id}>
                  <td><Badge bg="info">Traslado</Badge></td>
                  <td>{new Date(t.fecha).toLocaleDateString('es-CO')}</td>
                  <td><small>{t.cuenta_origen?.nombre || '—'}</small></td>
                  <td className="text-end fw-bold">{formatoCOP(t.monto)}</td>
                  <td><small>{t.descripcion || '—'}</small></td>
                  <td className="text-center">
                    <Button variant="outline-success" size="sm" className="me-1"
                      onClick={() => abrirConfirmacion(t, 'traslado')}
                      disabled={processingAction}>
                      <FaCheck />
                    </Button>
                    <Button variant="outline-danger" size="sm"
                      onClick={() => handleRechazar(t, 'traslado')}
                      disabled={processingAction}>
                      <FaBan />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Alert>
      )}

      {/* Acciones principales */}
      <Row className="mb-4 g-2">
        <Col xs="auto">
          <Button variant="primary" size="lg" onClick={() => setShowRegistrar(true)}>
            <FaPlus className="me-1" /> Registrar movimiento
          </Button>
        </Col>
        <Col xs="auto">
          <Button variant="outline-success" size="lg"
            onClick={handleCerrarGastos}
            disabled={
              processingAction ||
              (gastosRegistradosCount === 0 && !relacionBorradorActiva)
            }
            title={
              (gastosRegistradosCount === 0 && !relacionBorradorActiva)
                ? 'No hay gastos registrados en este periodo para cerrar.'
                : ''
            }>
            <FaClipboardCheck className="me-1" />
            {relacionBorradorActiva ? 'Continuar cierre en BORRADOR' : 'Cerrar gastos del período'}
          </Button>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="lg"
            onClick={() => navigate('/MisRelaciones')}>
            Mis relaciones
          </Button>
        </Col>
        <Col xs="auto">
          <Button variant="outline-info" size="lg"
            onClick={() => navigate('/MiBolsa/historial')}>
            <FaHistory className="me-1" /> Historial del bolsillo
          </Button>
        </Col>
      </Row>

      {/* Movimientos de Dinero — tabla dedicada (entregas + traslados) */}
      {movimientosDinero.length > 0 && (
        <Card className="mb-4 shadow-sm w-100">
          <Card.Header className="bg-info text-white w-100">
            <h5 className="mb-0">
              <FaExchangeAlt className="me-2" />
              Movimientos de Dinero ({movimientosDinero.length})
            </h5>
            <small>Entregas recibidas + traslados recibidos y enviados</small>
          </Card.Header>
          <Card.Body className="w-100">
            <Table hover responsive size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Consecutivo</th>
                  <th>Contraparte / Emisor</th>
                  <th className="text-end">Monto</th>
                  <th className="text-center">Estado</th>
                  <th>Descripción</th>
                  <th className="text-center">Obs.</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>


                {movimientosDinero.map((m) => {
                  const esEntrega = m.tipoMovimiento === 'ENTREGA';
                  const esRecibido = m.signo === '+';
                  const enBorradorRow = estaEnBorrador(
                    m.raw?._id,
                    esEntrega ? 'entregas' : 'traslados'
                  );
                  const tieneObs = esEntrega
                    ? tieneObservacionesEntrega(m.raw)
                    : tieneObservacionesTraslado(m.raw);
                  return (
                    <tr key={m._id}>
                      <td>{new Date(m.fecha).toLocaleDateString('es-CO')}</td>
                      <td>
                        {esEntrega ? (
                          <Badge bg="primary"><FaArrowDown className="me-1" />Entrega</Badge>
                        ) : esRecibido ? (
                          <Badge bg="success"><FaArrowDown className="me-1" />Traslado recibido</Badge>
                        ) : (
                          <Badge bg="warning"><FaArrowUp className="me-1" />Traslado enviado</Badge>
                        )}
                      </td>
                      <td>
                        {m.consecutivo ? (
                          <small className="text-muted">{m.consecutivo}</small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>

                      <td>
                        <small>{m.contraparte?.nombre || '—'}</small>
                        {m.contraparte?.codigo && (
                          <><br /><small className="text-muted">{m.contraparte.codigo}</small></>
                        )}
                        {m.contraparteLabel && (
                          <><br /><small className="text-muted">por {m.contraparteLabel}</small></>
                        )}
                      </td>


                      <td className={`text-end fw-bold ${esRecibido ? 'text-success' : 'text-warning'}`}>
                        {m.signo}{formatoCOP(m.monto)}
                      </td>
                      <td className="text-center">
                        <Badge bg={
                          m.estado === 'CONFIRMADO' || m.estado === 'CONFIRMADA' ? 'success' :
                          m.estado === 'PENDIENTE_CONFIRMACION' ? 'warning' :
                          m.estado === 'RECHAZADO' || m.estado === 'RECHAZADA' ? 'danger' : 'secondary'
                        }>
                          {m.estado.replace(/_/g, ' ')}
                        </Badge>
                        {enBorradorRow && (
                          <Badge bg="warning" text="dark" className="ms-1" title="Capturado en el borrador de cierre">
                            En borrador
                          </Badge>
                        )}
                      </td>
                      <td><small>{m.descripcion || '—'}</small></td>
                      <td className="text-center">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          disabled={!tieneObs}
                          title={tieneObs ? 'Ver observaciones' : 'Sin observaciones'}
                          onClick={() => abrirObservacionesDirecto(
                            m.raw,
                            esEntrega ? 'entrega' : 'traslado'
                          )}
                        >
                          <FaEye />
                        </Button>
                      </td>
                      <td className="text-center">
                        {m.puedeAnular ? (
                          <Button variant="outline-danger" size="sm"
                            onClick={() => handleAnularTraslado(m.raw)}
                            disabled={processingAction}
                            title="Anular traslado (solo el creador puede anular pendientes)">
                            <FaBan /> Anular
                          </Button>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Mis Gastos Registrados — Accordion por categoría */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <Row className="align-items-center">
            <Col md={8}>
              <h5 className="mb-0">
                <FaReceipt className="me-2" />
                Mis Gastos Registrados ({gastos.filter(g => g.estado !== 'ANULADO').length})
              </h5>
            </Col>
            <Col md={4} className="text-end">
              <Button variant="outline-primary" size="sm" onClick={() => setShowRegistrar(true)}>
                <FaPlus className="me-1" /> Agregar Gasto
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="w-100">
          {categoriasOrdenadas.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">
              <p className="mb-1">Aún no has registrado gastos en este bolsillo.</p>
              <small>Usa <strong>+ Agregar Gasto</strong> para registrar el primero.</small>
            </Alert>
          ) : (
            <Accordion>
              {categoriasOrdenadas.map(([categoria, datos], idx) => (
                <Accordion.Item key={categoria} eventKey={idx.toString()}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between w-100 me-3">
                      <div>
                        <strong>{formatearCategoria(categoria)}</strong>
                        <Badge bg="secondary" className="ms-2">{datos.cantidad}</Badge>
                      </div>
                      <div>
                        <strong className="text-primary">{formatoCOP(datos.total)}</strong>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table hover size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Fecha</th>
                          <th>Descripción</th>
                          <th>Tercero</th>
                          <th>Factura</th>
                          <th>OT</th>
                          <th className="text-end">Valor</th>
                          <th className="text-center">Estado</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {datos.items.map((g) => {
                          // Estado del tercero: verde si vinculado, amarillo si captura manual pendiente, rojo si nada (y la factura lo exige).
                          const tieneProveedor = !!g.proveedor;
                          const tieneManual = !!(g.proveedorSinCrear?.nit || g.proveedorSinCrear?.nombre);
                          const exige = !!g.tieneFactura;
                          const tercero = tieneProveedor
                            ? { icon: <FaCheckCircle className="text-success" />, label: g.proveedor.nombre || g.proveedor.nit, variant: 'ok' }
                            : tieneManual
                              ? { icon: <FaExclamationTriangle className="text-warning" />, label: `${g.proveedorSinCrear.nombre || ''} (${g.proveedorSinCrear.nit || 's/nit'})`, variant: 'pendiente' }
                              : { icon: <FaTimesCircle className={exige ? 'text-danger' : 'text-muted'} />, label: exige ? 'Sin tercero' : '—', variant: 'vacio' };
                          return (
                          <tr key={g._id}>
                            <td>{new Date(g.fecha).toLocaleDateString('es-CO')}</td>
                            <td><small>{g.descripcion}</small></td>
                            <td>
                              <small
                                title={tercero.variant === 'pendiente' ? 'Captura manual pendiente de crear como Proveedor real' : ''}
                                className="d-inline-flex align-items-center gap-1"
                              >
                                {tercero.icon}
                                <span>{tercero.label}</span>
                              </small>
                            </td>
                            <td>
                              {g.tieneFactura ? (
                                <Badge bg="primary">{g.numeroFactura || 'sí'}</Badge>
                              ) : (
                                <small className="text-muted">—</small>
                              )}
                            </td>
                            <td>
                              {g.ordenTrabajo?.numeroOT ? (
                                <small className="text-muted">{g.ordenTrabajo.numeroOT}</small>
                              ) : (
                                <small className="text-muted">—</small>
                              )}
                            </td>
                            <td className="text-end fw-bold">{formatoCOP(g.valorTotal)}</td>
                            <td className="text-center">
                              <Badge bg={
                                g.estado === 'REGISTRADO' ? 'success' :
                                g.estado === 'INCLUIDO_EN_RELACION' ? 'info' : 'secondary'
                              }>
                                {g.estado === 'REGISTRADO' ? 'Activo' :
                                 g.estado === 'INCLUIDO_EN_RELACION' ? 'En relación' : g.estado}
                              </Badge>
                              {estaEnBorrador(g._id, 'gastos') && (
                                <Badge bg="warning" text="dark" className="ms-1" title="Capturado en el borrador de cierre">
                                  En borrador
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">
                              {g.estado === 'REGISTRADO' ? (
                                <>
                                  <Button variant="outline-primary" size="sm" className="me-1"
                                    onClick={() => handleEditarGasto(g)}
                                    disabled={processingAction}
                                    title="Editar gasto">
                                    <FaEdit />
                                  </Button>
                                  <Button variant="outline-danger" size="sm"
                                    onClick={() => handleAnularGasto(g)}
                                    disabled={processingAction}
                                    title="Anular gasto">
                                    <FaTrash />
                                  </Button>
                                </>
                              ) : (
                                <small className="text-muted" title="Gasto incluido en una relación de gastos. No editable.">
                                  Bloqueado
                                </small>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Body>
      </Card>

      {/* Actividad reciente del ledger — plegable al fondo */}
      {actividadReciente && actividadReciente.length > 0 && (
        <Accordion className="mb-4">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <FaHistory className="me-2" />
              <span className="ms-1">Ver ledger completo del bolsillo (auditoría)</span>
              <Badge bg="secondary" className="ms-2">{actividadReciente.length}</Badge>
            </Accordion.Header>
            <Accordion.Body>
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th className="text-end">Monto</th>
                    <th className="text-end">Saldo posterior</th>
                    <th className="text-center">Obs.</th>
                  </tr>
                </thead>
                <tbody>
                  {actividadReciente.map((m) => {
                    // El ledger no contiene las observaciones — el FaEye hace
                    // fetch lazy del traslado/entrega original via
                    // m.origen.refId. Para tipos sin referencia (GastoContable,
                    // AjusteManual) el botón queda atenuado.
                    const refTipo = m?.origen?.tipo;
                    const refId   = m?.origen?.refId;
                    const habilitado = !!refId && (refTipo === 'EntregaDinero' || refTipo === 'TrasladoDinero');
                    return (
                      <tr key={m._id}>
                        <td>{new Date(m.fecha).toLocaleDateString('es-CO')}</td>
                        <td>
                          <Badge bg={m.signo === '+' ? 'success' : 'danger'}>
                            {m.tipo?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td><small>{m.descripcion || '—'}</small></td>
                        <td className={`text-end fw-bold ${m.signo === '+' ? 'text-success' : 'text-danger'}`}>
                          {m.signo}{formatoCOP(m.monto)}
                        </td>
                        <td className="text-end text-muted">{formatoCOP(m.saldoPosterior)}</td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            disabled={!habilitado || observacionesMov.loading}
                            title={habilitado ? 'Ver observaciones' : 'Sin observaciones'}
                            onClick={() => abrirObservacionesDesdeLedger(m)}
                          >
                            <FaEye />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {/* Modales */}
      {showRegistrar && (
        <ModalRegistrarMovimiento
          show={showRegistrar}
          onClose={cerrarModalRegistrar}
          bolsillo={bolsillo}
          gastoEditar={gastoEditar}
          onSuccess={onSuccessRegistrar}
        />
      )}

      {showConfirmacion && pendienteEnConfirmacion && (
        <ModalConfirmacionMovimiento
          show={showConfirmacion}
          onClose={() => setShowConfirmacion(false)}
          tipo={tipoEnConfirmacion}
          item={pendienteEnConfirmacion}
          processing={processingAction}
          onConfirmar={handleConfirmar}
        />
      )}

      {observacionesMov.show && (
        <ModalObservacionesMovimiento
          show={observacionesMov.show}
          onHide={cerrarObservaciones}
          movimiento={observacionesMov.movimiento}
          tipo={observacionesMov.tipo}
        />
      )}
    </Container>
  );
};

export default MiBolsa;
